// セクション描画直前の panel / total / key 解決をまとめる resolver です。
export function createSectionRenderModelResolver(deps) {
    // grid 表示設定を section ごとに安定化します。
    function toStableAutoHeightPanel(panelInfo, sectionId) {
        if (deps.isEmpty(panelInfo)) return panelInfo;

        if (deps.isDemandSectionId(sectionId)) {
            // 受注数/納品予定は内部スクロールへ切り替えず、全行表示を優先します。
            const normalizedType = deps.demandTypeKeyByPanel(panelInfo) || String(deps.DicVal(panelInfo, "type", ""));
            return { ...panelInfo, type: normalizedType, gridmax: "99999" };
        }
        if (sectionId === "genka") {
            // 原価は左右とも高さ不足でスクロールになりやすいため autoHeight を優先します。
            return { ...panelInfo, gridmax: "99999" };
        }
        return panelInfo;
    }

    // Grid 再生成キーを返します。
    function gridRenderKey(panelInfo) {
        if (deps.isEmpty(panelInfo)) return "";

        const base = deps.getGridId(panelInfo);
        const panelType = String(deps.DicVal(panelInfo, "type", ""));
        const panelId = String(deps.DicVal(panelInfo, "id", ""));
        const rowCount = deps.getGridRowCount(panelInfo);
        const columninfo = deps.getColumnInfo();
        const columns = deps.getColumns();

        if (panelType === deps.PANEL_TYPE.SIJISU || panelId === "1" || panelId === "sijisu") {
            const colinfoCount = Array.isArray(columninfo)
                ? columninfo.filter((e) => String(e.type ?? "") === deps.PANEL_TYPE.SIJISU).length
                : 0;
            const layoutCount = Array.isArray(columns)
                ? columns.filter((e) => String(e.type ?? "") === deps.PANEL_TYPE.SIJISU).length
                : 0;
            return `${base}_sijisu_${rowCount}_${colinfoCount}_${layoutCount}`;
        }

        const demandType = deps.demandTypeKeyByPanel(panelInfo);
        if (demandType === "") {
            // 副資材(114-117)/品質表示(119-123)はアイテム/グループ切替のたびに
            // グリッドインスタンスを作り直す。key変更でReactが旧インスタンスを破棄し、
            // 新インスタンスは dataRef の作り直したデータで初期化される。
            const contextKey = typeof deps.itemGroupContextRenderKey === "function"
                ? String(deps.itemGroupContextRenderKey(panelInfo) ?? "")
                : "";
            if (contextKey !== "") return `${base}_ctx_${contextKey}`;
            return base;
        }

        const typeCandidates = [demandType];
        const colinfoCount = Array.isArray(columninfo)
            ? columninfo.filter((e) => typeCandidates.includes(String(e.type ?? "").trim().toLowerCase())).length
            : 0;
        const layoutCount = Array.isArray(columns)
            ? columns.filter((e) => typeCandidates.includes(String(e.type ?? "").trim().toLowerCase())).length
            : 0;

        return `${base}_demand_${colinfoCount}_${layoutCount}_${deps.getKakouType()}`;
    }

    // section に対応する panel 群をまとめて返します。
    function resolveSectionPanels(section) {
        return {
            sectionPanel: deps.resolveSectionPanel(section),
            remarksSectionPanel: deps.resolveRemarksSectionPanel(section),
            gridSectionPanel: deps.resolveGridSectionPanel(section)
        };
    }

    // total 関連の描画情報をまとめます。
    function resolveSectionTotalModel(section, gridSectionPanel) {
        const totalHostPanel = deps.resolveTotalHostPanel(section, gridSectionPanel);
        const totalType = deps.sectionTotalType(section, gridSectionPanel);
        const resolvedTotalType = deps.normalizeTotalTypeForColumns(totalType, section.id);

        return {
            totalHostPanel,
            totalType,
            resolvedTotalType
        };
    }

    // 描画用の section モデルを 1 件組み立てます。
    function buildSectionRenderModel(section) {
        const {
            sectionPanel,
            remarksSectionPanel,
            gridSectionPanel
        } = resolveSectionPanels(section);

        const {
            totalHostPanel,
            totalType,
            resolvedTotalType
        } = resolveSectionTotalModel(section, gridSectionPanel);

        const stableGridPanel = toStableAutoHeightPanel(gridSectionPanel, section.id);
        const renderKey = gridRenderKey(stableGridPanel);

        return {
            section,
            sectionPanel,
            remarksSectionPanel,
            gridSectionPanel,
            totalHostPanel,
            totalType,
            resolvedTotalType,
            stableGridPanel,
            renderKey
        };
    }

    // 表示対象の section 群を描画モデルへ変換します。
    function buildSectionRenderModels(sections) {
        if (!Array.isArray(sections)) return [];
        return sections.map((section) => buildSectionRenderModel(section));
    }

    return {
        toStableAutoHeightPanel,
        gridRenderKey,
        resolveSectionPanels,
        resolveSectionTotalModel,
        buildSectionRenderModel,
        buildSectionRenderModels
    };
}
